// SPDX-License-Identifier: GPL-3.0-or-later
// Label textures for the SigmaBot characters (THEME.md section 5). All text is #FFFFFF on #000000,
// monospace bold. Text is never red. Red is a fill only.

import { BRAND, type Ctx2DLike, monoFont } from "./canvas.ts";

/** Average width of a monospace character, as a part of the font size. */
const CHAR_WIDTH = 0.62;

function fillBackground(ctx: Ctx2DLike, w: number, h: number): void {
  ctx.globalAlpha = 1;
  ctx.fillStyle = BRAND.field;
  ctx.fillRect(0, 0, w, h);
}

/** Font size in pixels so that every line fits in the box. */
export function fitFontSize(lines: readonly string[], w: number, h: number): number {
  const longest = Math.max(1, ...lines.map((l) => l.length));
  const byWidth = (w * 0.9) / (longest * CHAR_WIDTH);
  const byHeight = (h * 0.8) / Math.max(1, lines.length);
  return Math.floor(Math.min(byWidth, byHeight));
}

/** White text lines on black, centered. */
export function drawTextPanel(
  ctx: Ctx2DLike,
  w: number,
  h: number,
  lines: readonly string[],
): void {
  fillBackground(ctx, w, h);
  const size = fitFontSize(lines, w, h);
  ctx.font = monoFont(size);
  ctx.fillStyle = BRAND.edge;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const step = size * 1.15;
  const top = h / 2 - (step * (lines.length - 1)) / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, w / 2, top + i * step);
  });
}

/** Split a label at the space nearest to the middle when it is longer than `max` characters. */
export function splitLabel(label: string, max: number): string[] {
  if (label.length <= max) return [label];
  const mid = label.length / 2;
  let best = -1;
  for (let i = 0; i < label.length; i++) {
    if (label[i] === " " && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
  }
  if (best < 0) return [label];
  return [label.slice(0, best), label.slice(best + 1)];
}

/** Belly panel of Rex: a stylized command label and a cursor block. White on black. */
export function drawCommandPanel(
  ctx: Ctx2DLike,
  w: number,
  h: number,
  label: string,
  cursorOn: boolean,
): void {
  fillBackground(ctx, w, h);
  const lines = splitLabel(label, 12);
  const size = fitFontSize(lines, w * 0.85, h * 0.75);
  ctx.font = monoFont(size);
  ctx.fillStyle = BRAND.edge;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const step = size * 1.2;
  const top = h / 2 - (step * (lines.length - 1)) / 2;
  const left = w * 0.08;
  lines.forEach((line, i) => {
    ctx.fillText(line, left, top + i * step);
  });
  if (cursorOn) {
    const last = lines[lines.length - 1] ?? "";
    const x = left + last.length * size * CHAR_WIDTH + size * 0.15;
    const y = top + (lines.length - 1) * step;
    ctx.fillStyle = BRAND.edge;
    ctx.fillRect(x, y - size * 0.45, size * 0.5, size * 0.9);
  }
}

/** The four lane letters on the clipboard of Larry. */
export const LANE_LETTERS = ["L", "G", "T", "M"] as const;

/**
 * The clipboard of Larry. An unlit letter is a white outline letter.
 * A lit letter is a white letter on a filled red square. The letter is white in both states.
 */
export function drawClipboard(ctx: Ctx2DLike, w: number, h: number, lit: readonly boolean[]): void {
  fillBackground(ctx, w, h);
  const row = h / LANE_LETTERS.length;
  const size = Math.floor(row * 0.7);
  ctx.font = monoFont(size);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  LANE_LETTERS.forEach((letter, i) => {
    const cy = row * i + row / 2;
    if (lit[i] === true) {
      const box = row * 0.86;
      ctx.fillStyle = BRAND.mark;
      ctx.fillRect(w / 2 - box / 2, cy - box / 2, box, box);
      ctx.fillStyle = BRAND.edge;
      ctx.fillText(letter, w / 2, cy);
    } else {
      ctx.strokeStyle = BRAND.edge;
      ctx.lineWidth = 2;
      ctx.strokeText(letter, w / 2, cy);
    }
  });
}
